import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ExitIntentPopup } from "@/components/ExitIntentPopup";
import { useEffect, lazy, Suspense } from "react";

// Eagerly load HomePage for best LCP
import HomePage from "./pages/HomePage";

// Lazy load all other pages to reduce initial bundle size
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const WholesalePage = lazy(() => import("./pages/WholesalePage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const BenefitsPage = lazy(() => import("./pages/BenefitsPage"));
const DosingGuidePage = lazy(() => import("./pages/DosingGuidePage"));
const SciencePage = lazy(() => import("./pages/SciencePage"));
const CustomerService = lazy(() => import("./pages/CustomerService"));
const About = lazy(() => import("./pages/About"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const ShippingPolicy = lazy(() => import("./pages/policies/ShippingPolicy"));
const PrivacyPolicy = lazy(() => import("./pages/policies/PrivacyPolicy"));
const ReturnPolicy = lazy(() => import("./pages/policies/ReturnPolicy"));
const RefundPolicy = lazy(() => import("./pages/policies/RefundPolicy"));
const TermsOfService = lazy(() => import("./pages/policies/TermsOfService"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Minimal loading fallback to avoid layout shift
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Component to track page views
const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Fire Google Ads Page View conversion on every route change
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'conversion', {
        'send_to': 'AW-17766725406/dWbpCL-Rj8kbEJ7u6pdC',
        'value': 1.0,
        'currency': 'USD'
      });
    }
  }, [location]);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <PageViewTracker />
        <ExitIntentPopup />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/product/:handle" element={<ProductDetail />} />
            <Route path="/wholesale" element={<WholesalePage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/benefits" element={<BenefitsPage />} />
            <Route path="/dosing-guide" element={<DosingGuidePage />} />
            <Route path="/science" element={<SciencePage />} />
            <Route path="/customer-service" element={<CustomerService />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/policies/shipping" element={<ShippingPolicy />} />
            <Route path="/policies/privacy" element={<PrivacyPolicy />} />
            <Route path="/policies/returns" element={<ReturnPolicy />} />
            <Route path="/policies/refund" element={<RefundPolicy />} />
            <Route path="/policies/terms" element={<TermsOfService />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
